import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@/hooks/useTheme'
import { AuthProvider } from '@/hooks/useAuth'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { MainLayout } from '@/components/layout/MainLayout'
import { Spinner } from '@/components/ui/spinner'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// Lazy load pages for code splitting (bundle-dynamic-imports)
const LoginPage = lazy(() => import('@/pages/Login').then(m => ({ default: m.LoginPage })))
const OnboardingPage = lazy(() => import('@/pages/Onboarding').then(m => ({ default: m.OnboardingPage })))
const InviteAcceptPage = lazy(() => import('@/pages/InviteAccept').then(m => ({ default: m.InviteAcceptPage })))
const DashboardPage = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.DashboardPage })))
const ActionsPage = lazy(() => import('@/pages/Actions').then(m => ({ default: m.ActionsPage })))
const KanbanPage = lazy(() => import('@/pages/Kanban').then(m => ({ default: m.KanbanPage })))
const CalendarPage = lazy(() => import('@/pages/Calendar').then(m => ({ default: m.CalendarPage })))
const ContactDetailPage = lazy(() => import('@/pages/ContactDetail').then(m => ({ default: m.ContactDetailPage })))
const ContactsPage = lazy(() => import('@/pages/Contacts').then(m => ({ default: m.ContactsPage })))
const CompaniesPage = lazy(() => import('@/pages/Companies').then(m => ({ default: m.CompaniesPage })))
const DealsPage = lazy(() => import('@/pages/Deals').then(m => ({ default: m.DealsPage })))
const SettingsPage = lazy(() => import('@/pages/Settings').then(m => ({ default: m.SettingsPage })))
const InboxPage = lazy(() => import('@/pages/Inbox').then(m => ({ default: m.InboxPage })))
const MessagesPage = lazy(() => import('@/pages/Messages').then(m => ({ default: m.MessagesPage })))
const SupportPipelinePage = lazy(() => import('@/pages/SupportPipeline').then(m => ({ default: m.SupportPipelinePage })))
const SaasAdminSupportDetailPage = lazy(() => import('@/pages/SaasAdminSupportDetail').then(m => ({ default: m.SaasAdminSupportDetailPage })))

// AI Workflow Pages (default exports)
const DailyActionsPage = lazy(() => import('@/pages/DailyActions'))
const CustomerInsightsPage = lazy(() => import('@/pages/CustomerInsights'))
const OnboardingProgressPage = lazy(() => import('@/pages/OnboardingProgress'))
const EmailDraftEditorPage = lazy(() => import('@/pages/EmailDraftEditor'))
const ContextGraphPage = lazy(() => import('@/pages/ContextGraph'))
const OAuthCallbackPage = lazy(() => import('@/pages/OAuthCallback').then(m => ({ default: m.OAuthCallbackPage })))
const AuthVerifyPage = lazy(() => import('@/pages/AuthVerify').then(m => ({ default: m.AuthVerifyPage })))
const ProjectOnboardingPage = lazy(() => import('@/components/projects/ProjectOnboardingWizard').then(m => ({ default: m.ProjectOnboardingWizard })))
const PagesPage = lazy(() => import('@/pages/Pages').then(m => ({ default: m.PagesPage })))
const BookingSettingsPage = lazy(() => import('@/pages/BookingSettings').then(m => ({ default: m.BookingSettingsPage })))
const PublicBookingPage = lazy(() => import('@/pages/PublicBooking').then(m => ({ default: m.PublicBookingPage })))
const AnalyticsPage = lazy(() => import('@/pages/Analytics'))

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Spinner className="w-8 h-8" />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/verify" element={<AuthVerifyPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />
              <Route path="/book/:eventTypeId" element={<PublicBookingPage />} />

              {/* Protected full-screen routes (no layout) */}
              <Route element={<ProtectedRoute />}>
                <Route path="/projects/new" element={<ProjectOnboardingPage />} />
              </Route>

              {/* Protected routes with layout */}
              <Route element={<ProtectedRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/actions" element={<ActionsPage />} />
                  <Route path="/kanban" element={<KanbanPage />} />
                  <Route path="/tasks" element={<KanbanPage />} />
                  <Route path="/pages" element={<PagesPage />} />
                  <Route path="/pages/:pageId" element={<PagesPage />} />
                  <Route path="/calendar" element={<CalendarPage />} />
                  <Route path="/email" element={<InboxPage />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/pipelines" element={<DealsPage />} />
                  <Route path="/pipelines/sales" element={<DealsPage />} />
                  <Route path="/pipelines/support" element={<SupportPipelinePage />} />
                  {/* Redirect old saas-admin/support to pipelines/support */}
                  <Route path="/saas-admin/support" element={<Navigate to="/pipelines/support" replace />} />
                  <Route path="/saas-admin/support/:ticketId" element={<SaasAdminSupportDetailPage />} />
                  <Route path="/records" element={<ContactsPage />} />
                  <Route path="/records/contacts" element={<ContactsPage />} />
                  <Route path="/records/companies" element={<CompaniesPage />} />
                  <Route path="/records/contacts/:id" element={<ContactDetailPage />} />
                  <Route path="/calendar/booking" element={<BookingSettingsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />

                  {/* AI Workflow Pages */}
                  <Route path="/daily-actions" element={<DailyActionsPage />} />
                  <Route path="/insights" element={<CustomerInsightsPage />} />
                  <Route path="/onboarding-progress" element={<OnboardingProgressPage />} />
                  <Route path="/drafts" element={<EmailDraftEditorPage />} />
                  <Route path="/drafts/:id" element={<EmailDraftEditorPage />} />
                  <Route path="/context-graph" element={<ContextGraphPage />} />
                  <Route path="/analytics" element={<AnalyticsPage />} />
                </Route>
              </Route>

              {/* Redirect unknown routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
